import { AtgChip } from "@/components/AtgChip";
import { PlayerName } from "@/components/PlayerCardDialog";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Circle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  APPROVED: "border-lime-600/60 bg-lime-950/30 text-lime-300",
  WATCH: "border-amber-700/60 bg-amber-950/30 text-amber-300",
};
const STATUS_EMOJI = { APPROVED: "🟢", WATCH: "🟡", PASS: "🔴" };

const PerfStat = ({ label, value, tone = "text-slate-100" }) => (
  <div className="flex flex-col items-center rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2">
    <span className="font-mono text-[8px] uppercase tracking-widest text-slate-500">{label}</span>
    <span className={`font-display text-xl font-bold tabular-nums ${tone}`}>{value}</span>
  </div>
);

const PerfGroup = ({ title, accent, g, extraLabel, extra }) => (
  <div className={`rounded-xl border p-3 ${accent}`} data-testid={`perf-group-${title.toLowerCase()}`}>
    <div className="mb-2 flex items-center justify-between">
      <h3 className="font-display text-[13px] font-bold uppercase tracking-wide">{title}</h3>
      <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{extraLabel}</span>
    </div>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {g.map(([l, v, t]) => <PerfStat key={l} label={l} value={v} tone={t} />)}
    </div>
    {extra}
  </div>
);

const PerformanceTracker = ({ perf }) => {
  if (!perf) return null;
  const a = perf.approved, w = perf.watch, c = perf.capture;
  return (
    <div className="space-y-3" data-testid="performance-tracker">
      <PerfGroup
        title="Approved"
        accent="border-lime-700/50 bg-lime-950/15"
        extraLabel="Official picks that scored ÷ total"
        g={[
          ["Picks", a.picks],
          ["Hits", a.hits, "text-lime-300"],
          ["Misses", a.misses, "text-rose-300"],
          ["Hit Rate", `${a.hit_rate}%`, "text-lime-300"],
        ]}
      />
      <PerfGroup
        title="Watch"
        accent="border-amber-700/50 bg-amber-950/15"
        extraLabel="Watch picks that scored ÷ total"
        g={[
          ["Picks", w.picks],
          ["Hits", w.hits, "text-amber-300"],
          ["Misses", w.misses, "text-rose-300"],
          ["Hit Rate", `${w.hit_rate}%`, "text-amber-300"],
        ]}
      />
      <PerfGroup
        title="Capture"
        accent="border-cyan-700/50 bg-cyan-950/15"
        extraLabel="Actual scorers captured by Approved OR Watch"
        g={[
          ["Actual Scorers", c.total_scorers],
          ["Approved Cap.", c.approved_captured, "text-lime-300"],
          ["Watch Cap.", c.watch_captured, "text-amber-300"],
          ["Capture Rate", `${c.capture_rate}%`, "text-cyan-300"],
        ]}
        extra={
          <p className="mt-2 text-center font-mono text-[10px] text-slate-500">
            <span className="text-slate-300">{c.total_captured}</span> captured ÷ <span className="text-slate-300">{c.total_scorers}</span> actual scorers = <span className="font-bold text-cyan-300">{c.capture_rate}%</span>
          </p>
        }
      />
    </div>
  );
};

const ENGINES = [
  ["goal_origin", "Goal Origin"],
  ["goalie_origin", "Goalie Origin"],
  ["line_match", "Line Match"],
  ["matchup", "Matchup"],
  ["pp", "PP"],
  ["pairing", "Pairing"],
  ["archetype", "Archetype"],
  ["numerology", "Numerology"],
];

export default function OfficialCardTab({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [approvedOnly, setApprovedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = apiDate ? { date: apiDate } : {};
      const res = await axios.get(`${API}/official-card`, { params, timeout: 240000 });
      setData(res.data);
    } catch (e) { console.error(e); setError(true); }
    finally { setLoading(false); }
  }, [apiDate]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="official-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Compiling official card…</p>
    </div>
  );
  if (error || !data) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="official-error">
      <p className="font-mono text-xs uppercase tracking-widest text-rose-400">Official card unavailable</p>
      <button onClick={load} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
    </div>
  );

  const targets = approvedOnly ? data.targets.filter((t) => t.status === "APPROVED") : data.targets;

  return (
    <div className="space-y-4" data-testid="official-card-dashboard">
      <div className="rounded-xl border border-lime-900/40 bg-gradient-to-r from-lime-950/20 to-slate-900/20 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wide text-lime-300">✅ Official Card</h2>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">Only APPROVED selections count · numerology never approves</p>
          </div>
          <div className="flex flex-col items-center rounded-lg border border-lime-700/50 bg-lime-950/30 px-5 py-2">
            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Approved</span>
            <span className="font-display text-2xl font-bold text-lime-300" data-testid="approved-count">{data.approved_count}</span>
          </div>
        </div>
        {/* engine status */}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="engine-status">
          {ENGINES.map(([k, label]) => (
            <span key={k} className={`flex items-center gap-1 rounded-lg border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider ${data.engine_status[k] ? "border-lime-800/60 bg-lime-950/20 text-lime-300" : "border-slate-800 bg-slate-900/40 text-slate-600"}`}>
              {data.engine_status[k] ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {label}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          data-testid="official-approved-toggle"
          onClick={() => setApprovedOnly((a) => !a)}
          className={`rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${approvedOnly ? "bg-lime-500/20 text-lime-300 shadow-[inset_0_0_0_1px_rgba(163,230,53,0.45)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"}`}
        >
          Approved Only
        </button>
        <button
          data-testid="official-refresh"
          onClick={load}
          className="flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-cyan-300 hover:bg-cyan-950/60"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Live Audit
        </button>
      </div>

      {/* APPROVED / WATCH / CAPTURE performance tracker */}
      <PerformanceTracker perf={data.performance} />

      {/* single targets */}
      <div className="overflow-hidden rounded-xl border border-slate-800" data-testid="official-targets">
        <div className="bg-slate-900/80 px-3 py-2 font-display text-[13px] font-bold uppercase tracking-wide text-slate-100">Goal Targets</div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="bg-slate-900/60 font-mono text-[9px] uppercase tracking-wider text-slate-500">
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Player</th>
                <th className="px-2 py-1.5 text-left">Line</th>
                <th className="px-2 py-1.5 text-left">Game</th>
                <th className="px-2 py-1.5 text-left">Goalie</th>
                <th className="px-2 py-1.5 text-right">Origin</th>
                <th className="px-2 py-1.5 text-right">Score</th>
                <th className="px-2 py-1.5 text-right">Result</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t, i) => (
                <tr key={`${t.team}-${t.jersey}-${i}`} className="border-t border-slate-800/60" data-testid={`official-target-${t.jersey}`}>
                  <td className="px-2 py-1.5">
                    <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${STATUS_STYLE[t.status] || "border-slate-700 text-slate-400"}`}>{STATUS_EMOJI[t.status]} {t.status}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="font-mono text-[11px] font-bold text-cyan-400">{t.jersey != null ? `#${t.jersey}` : "#—"}</span>{" "}
                    <PlayerName name={t.player} team={t.team} className="font-display text-[12px] font-semibold uppercase text-slate-100" />
                    <AtgChip atg={t.atg} testId={`card-atg-${t.team}-${t.jersey}`} />
                    <span className="ml-1 rounded bg-slate-800 px-1 font-mono text-[9px] text-slate-400">{t.position}</span>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-cyan-300">{t.slot}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">{t.game}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-lime-500/80">{String(t.goalie || "").split(" ").slice(-1)[0]}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[11px] font-bold text-lime-300 tabular-nums">{t.origin_goals}G/{t.origin_games}GM</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[12px] font-bold text-cyan-200 tabular-nums">{t.score}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right">
                    <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${t.scored ? "bg-lime-500 text-slate-950" : "bg-slate-800 text-slate-500"}`} data-testid={`official-result-${t.jersey}`}>
                      {t.scored ? `GOAL ✓${t.goals > 1 ? ` ×${t.goals}` : ""}` : "NO GOAL"}
                    </span>
                  </td>
                </tr>
              ))}
              {targets.length === 0 && <tr><td colSpan={8} className="py-8 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600">No qualifying targets</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* approved pairs */}
      <div className="overflow-hidden rounded-xl border border-slate-800" data-testid="official-pairs">
        <div className="bg-slate-900/80 px-3 py-2 font-display text-[13px] font-bold uppercase tracking-wide text-slate-100">Approved Pairs <span className="ml-1 font-mono text-[9px] font-normal normal-case text-slate-500">from the Official NHL Pair Board (master engine)</span></div>
        <div className="divide-y divide-slate-800/60">
          {data.pairs.length ? data.pairs.map((p) => (
            <div key={p.pair_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`official-pair-${p.pair_id}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="rounded border border-lime-600/60 bg-lime-950/30 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-lime-300">🟢 {p.status}</span>
                <PlayerName name={p.player_a.name} team={p.player_a.team} className="font-display text-[12px] font-semibold uppercase text-slate-100" />
                <span className="text-slate-600">+</span>
                <PlayerName name={p.player_b.name} team={p.player_b.team} className="font-display text-[12px] font-semibold uppercase text-slate-100" />
                <span className="font-mono text-[9px] uppercase text-slate-500">{p.primary_type} · {p.game_label}</span>
              </div>
              <span className="font-mono text-[13px] font-bold text-cyan-200 tabular-nums">{p.pair_score}</span>
            </div>
          )) : <p className="py-8 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600">No approved pairs</p>}
        </div>
      </div>
    </div>
  );
}
